import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { faChevronCircleUp } from "@fortawesome/free-solid-svg-icons/faChevronCircleUp";
import {
    DASHBOARD_URL,
    WORKFLOWS_URL,
    WORKFLOWS_DETAILS_URL,
    VERIFICATION_SERVICES_URL,
    IDENTITY_PROVIDERS_URL,
    IDENTITY_PROVIDERS_DETAILS_URL,
    ORGANIZATIONS_URL,
    ORGANIZATION_DETAILS_URL,
    ORGANIZATION_USERS_URL,
    ORGANIZATION_USERS_DETAILS_URL,
    ORGANIZATION_ACTIVITY_URL,
    ORGANIZATION_INVITATIONS_URL,
    ORGANIZATION_INVITATION_DETAILS_URL,
    RELYING_PARTIES_URL,
    RELYING_PARTIES_DETAIL_URL,
    ALERT_EVENTS_URL,
    REPORTS_TRANSACTIONS_URL,
    REPORTS_TRANSACTIONS_DETAILS_URL,
    REPORTS_SERVICE_ASSERTION_PERFORMANCE_URL,
    REPORTS_SERVICE_ASSERTION_PERFORMANCE_COMPARISON_URL,
    REPORTS_WORKFLOW_USAGE_DETAILS_URL,
    REPORTS_WORKFLOW_REPORT_URL,
    REPORTS_FRAUD_REPORT_URL,
    REPORTS_USER_SUMMARY_URL,
    REPORTS_ADAPTIVE_AUTHENTICATION_URL,
    MANAGEMENT_CONSOLE_AXN_CACHE_URL,
    MANAGEMENT_CONSOLE_AXN_CONFIG_URL,
    MANAGEMENT_CONSOLE_AXN_SESSIONS_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_CACHE_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_CONFIG_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_LOGGING_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_DETAILS_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_SESSIONS_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_DETAILS_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_DETAILS_URL,
    DISCLOSURES_URL,
    HELP_URL,
    VERIFICATION_SERVICES_DETAILS_URL,
    POLICY_COMPONENTS_URL,
    POLICY_COMPONENT_DETAILS_URL,
    MESSAGING_TEMPLATES_URL,
    MESSAGING_TEMPLATES_DETAILS_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_DETAILS_URL,
    EMAIL_TEMPLATES_DETAILS_URL,
    EMAIL_TEMPLATES_URL
} from "@constants/routes";
import { DashboardContainer } from "../dashboard/dashboard-container";
import { WorkflowsContainer } from "../workflows/workflows-container";
import { WorkflowDetailsContainer } from "../workflow-details/workflow-details-container";
import { DisclosuresContainer } from "../disclosures/disclosures-container";
import { AxnCacheContainer } from "../management-console/axn-cache/axn-cache-container";
import { AxnConfigContainer } from "../management-console/axn-config/axn-config-container";
import { AxnSessionsContainer } from "../management-console/axn-sessions/axn-sessions-container";
import { AxnAdminCacheContainer } from "../management-console/axn-admin-cache/axn-admin-cache-container";
import { AxnAdminConfigContainer } from "../management-console/axn-admin-config/axn-admin-config-container";
import { AxnAdminLoggingContainer } from "../management-console/axn-admin-logging/axn-admin-logging-container";
import { SecurityRuleDetailsContainer } from "../management-console/security-rule-details/security-rule-details-container";
import { SecurityRulesContainer } from "../management-console/security-rules/security-rules-container";
import { AxnAdminSessionsContainer } from "../management-console/axn-admin-sessions/axn-admin-sessions-container";
import { AttributeLabelDetailsContainer } from "../management-console/attribute-label-details/attribute-label-details-container";
import { AttributeLabelsContainer } from "../management-console/attribute-labels/attribute-labels-container";
import { HelpContainer } from "../help/help-container";
import { ClickableIcon } from "../common/clickable-icon/clickable-icon";
import { RelyingPartiesContainer } from "../relying-parties/relying-parties-container";
import { RelyingPartyDetailsContainer } from "../relying-party-details/relying-party-details-container";
import { IdentityProvidersContainer } from "../identity-providers/identity-providers-container";
import { IdentityProviderDetailsContainer } from "../identity-provider-details/identity-provider-details-container";
import { OrganizationsContainer } from "../organization/organizations/organizations-container";
import { OrganizationDetailsContainer } from "../organization/organization-details/organization-details-container";
import { OrganizationUsersContainer } from "../organization/organization-users/organization-users-container";
import { OrganizationUserDetailsContainer } from "../organization/organization-user-details/organization-user-details-container";
import { OrganizationActivityContainer } from "../organization/organization-activity/organization-activity-container";
import { OrganizationInvitationsContainer } from "../organization/organization-invitations/organization-invitations-container";
import { OrganizationInvitationDetailsContainer } from "../organization/organization-invitation-details/organization-invitation-details-container";
import { AlertEventsContainer } from "../alert-events/alert-events-container";
import { AdapativeAuthenticationContainer } from "../reports/adaptive-authentication/adaptive-authentication-container";
import { UserSummaryContainer } from "../reports/user-summary/user-summary-container";
import { FraudReportContainer } from "../reports/fraud-report/fraud-report-container";
import { WorkflowUsageDetailsContainer } from "../reports/workflow-usage-details/workflow-usage-details-container";
import { WorkflowReportContainer } from "../reports/workflow-report/workflow-report-container";
import { ServiceAssertionPerformanceContainer } from "../reports/service-assertion-performance/service-assertion-performance-container";
import { ServiceAssertionPerformanceComparisonContainer } from "../reports/service-assertion-performance-comparison/service-assertion-performance-comparison-container";
import { VerificationServicesContainer } from "../verification-services/verification-services-container";
import { VerificationServiceDetailsContainer } from "../verification-service-details/verification-service-details-container";
import { VerificationTransactionsContainer } from "../reports/verification-transactions/verification-transactions-container";
import { VerificationTransactionDetailsContainer } from "../reports/verification-transaction-details/verification-transaction-details-container";
import { WorkflowTemplatesContainer } from "../management-console/workflow-templates/workflow-templates-container";
import { WorkflowTemplateDetailsContainer } from "../management-console/workflow-template-details/workflow-template-details-container";
import { PolicyComponentsContainer } from "@containers/policy-components/policy-components-container";
import { PolicyComponentDetailsContainer } from "@containers/policy-component-details/policy-component-details-container";
import { MessagingTemplatesContainer } from "@containers/messaging-templates/messaging-templates-container";
import { MessagingTemplateDetailsContainer } from "@containers/messaging-template-details/messaging-template-details-container";
import { ErrorBoundary } from "../common/error-boundary/error-boundary";
import { useSessionMonitor } from "@hooks/use-session-monitor";
import { IDWSession } from "@idw-types/data/idw-session";
import styles from "./content-container.less";
import { ServiceTemplatesContainer } from "@containers/management-console/service-templates/service-templates-container";
import { ServiceTemplateDetailsContainer } from "@containers/management-console/service-template-details/service-template-details-container";
import { EmailTemplatesContainer } from "@containers/email-templates/email-templates-container";
import { EmailTemplateDetailsContainer } from "@containers/email-template-details/email-template-details-container";

type ContentContainerProps = {
    session: IDWSession;
    isNavigationMenuCollapsed: boolean;
};

export const ContentContainer = (props: ContentContainerProps) => {
    const { session, isNavigationMenuCollapsed } = props;
    const contentRef = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();
    const [showScrollToTop, setShowScrollToTop] = useState(false);

    const scrollToTop = () => {
        if (contentRef.current) {
            if (contentRef.current.scrollTo) {
                contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                contentRef.current.scrollTop = 0;
            }
        }
    }

    const scrollHandler = debounce(() => {
        if (contentRef.current) {
            setShowScrollToTop(contentRef.current.scrollTop > 200);
        }
    }, 100);
    useSessionMonitor();

    //if url changes, scroll to the top
    useEffect(() => {
        scrollToTop();
    }, [pathname]);

    const additionalClassName = useMemo(() => {
        return isNavigationMenuCollapsed ? styles.wideContentContainer : "";
    }, [isNavigationMenuCollapsed]);

    return (
        <div ref={contentRef} className={classNames(styles.contentContainer, additionalClassName)} onScroll={scrollHandler}>
            <div className={styles.content}>
                <ErrorBoundary session={session}>
                    <Routes>
                        <Route path={DASHBOARD_URL} element={<DashboardContainer />} />
                        {session.userPermissions?.canListWorkflows && (
                            <>
                                <Route path={WORKFLOWS_URL} element={<WorkflowsContainer />} />
                                <Route path={WORKFLOWS_DETAILS_URL} element={<WorkflowDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListRPServices && (
                            <>
                                <Route path={VERIFICATION_SERVICES_URL} element={<VerificationServicesContainer />} />
                                <Route path={VERIFICATION_SERVICES_DETAILS_URL} element={<VerificationServiceDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListIdentityProviders && (
                            <>
                                <Route path={IDENTITY_PROVIDERS_URL} element={<IdentityProvidersContainer />} />
                                <Route path={IDENTITY_PROVIDERS_DETAILS_URL} element={<IdentityProviderDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListOrganizations && (
                            <>
                                <Route path={ORGANIZATIONS_URL} element={<OrganizationsContainer />} />
                                <Route path={ORGANIZATION_DETAILS_URL} element={<OrganizationDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListUsers && (
                            <>
                                <Route path={ORGANIZATION_USERS_URL} element={<OrganizationUsersContainer />} />
                                <Route path={ORGANIZATION_USERS_DETAILS_URL} element={<OrganizationUserDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListUserInvitations && (
                            <>
                                <Route path={ORGANIZATION_INVITATIONS_URL} element={<OrganizationInvitationsContainer />} />
                                <Route path={ORGANIZATION_INVITATION_DETAILS_URL} element={<OrganizationInvitationDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListOrganizationActivity && (
                            <Route path={ORGANIZATION_ACTIVITY_URL} element={<OrganizationActivityContainer />} />
                        )}
                        {session.userPermissions?.canListMessagingTemplates && (
                            <>
                                <Route path={MESSAGING_TEMPLATES_URL} element={<MessagingTemplatesContainer />} />
                                <Route path={MESSAGING_TEMPLATES_DETAILS_URL} element={<MessagingTemplateDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListSelfServiceVerificationEmailTemplates && (
                            <>
                                <Route path={EMAIL_TEMPLATES_URL} element={<EmailTemplatesContainer />} />
                                <Route path={EMAIL_TEMPLATES_DETAILS_URL} element={<EmailTemplateDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListRelyingParties && (
                            <>
                                <Route path={RELYING_PARTIES_URL} element={<RelyingPartiesContainer />} />
                                <Route path={RELYING_PARTIES_DETAIL_URL} element={<RelyingPartyDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListPolicyComponents && (
                            <>
                                <Route path={POLICY_COMPONENTS_URL} element={<PolicyComponentsContainer />} />
                                <Route path={POLICY_COMPONENT_DETAILS_URL} element={<PolicyComponentDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canListAlertEvents && (
                            <Route path={ALERT_EVENTS_URL} element={<AlertEventsContainer />} />
                        )}
                        {session.userPermissions?.canViewTransactionReport && (
                            <>
                                <Route path={REPORTS_TRANSACTIONS_URL} element={<VerificationTransactionsContainer />} />
                                <Route path={REPORTS_TRANSACTIONS_DETAILS_URL} element={<VerificationTransactionDetailsContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canViewServicePerformanceReport && (
                            <>
                                <Route path={REPORTS_SERVICE_ASSERTION_PERFORMANCE_URL} element={<ServiceAssertionPerformanceContainer />} />
                                <Route path={REPORTS_SERVICE_ASSERTION_PERFORMANCE_COMPARISON_URL} element={<ServiceAssertionPerformanceComparisonContainer />} />
                            </>
                        )}
                        {session.userPermissions?.canViewWorkflowUsageReport && (
                            <Route path={REPORTS_WORKFLOW_USAGE_DETAILS_URL} element={<WorkflowUsageDetailsContainer />} />
                        )}
                        {session.userPermissions?.canViewWorkflowReport && (
                            <Route path={REPORTS_WORKFLOW_REPORT_URL} element={<WorkflowReportContainer />} />
                        )}
                        {session.userPermissions?.canViewFraudReport && (
                            <Route path={REPORTS_FRAUD_REPORT_URL} element={<FraudReportContainer />} />
                        )}
                        {session.userPermissions?.canViewUserSummaryReport && (
                            <Route path={REPORTS_USER_SUMMARY_URL} element={<UserSummaryContainer />} />
                        )}
                        {session.userPermissions?.canViewAdaptiveAuthenticationReport && (
                            <Route path={REPORTS_ADAPTIVE_AUTHENTICATION_URL} element={<AdapativeAuthenticationContainer />} />
                        )}
                        {session.userPermissions?.canAccessManagementConsole && (
                            <>
                                <Route path={MANAGEMENT_CONSOLE_AXN_CACHE_URL} element={<AxnCacheContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_CACHE_URL} element={<AxnCacheContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_CONFIG_URL} element={<AxnConfigContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_SESSIONS_URL} element={<AxnSessionsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_CACHE_URL} element={<AxnAdminCacheContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_CONFIG_URL} element={<AxnAdminConfigContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_LOGGING_URL} element={<AxnAdminLoggingContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_SESSIONS_URL} element={<AxnAdminSessionsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_DETAILS_URL} element={<AttributeLabelDetailsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL} element={<AttributeLabelsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_DETAILS_URL} element={<WorkflowTemplateDetailsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL} element={<WorkflowTemplatesContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL} element={<ServiceTemplatesContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_DETAILS_URL} element={<ServiceTemplateDetailsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_DETAILS_URL} element={<SecurityRuleDetailsContainer />} />
                                <Route path={MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL} element={<SecurityRulesContainer />} />
                            </>
                        )}
                        <Route path={DISCLOSURES_URL} element={<DisclosuresContainer />} />
                        <Route path={HELP_URL} element={<HelpContainer />} />
                        <Route path={"*"} element={<Navigate to={DASHBOARD_URL} />} />
                    </Routes>
                </ErrorBoundary>
            </div>
            <ClickableIcon
                controlId={'scroll-to-top'}
                onClick={scrollToTop}
                icon={faChevronCircleUp}
                className={classNames(styles.scrollToTopIcon, showScrollToTop ? "" : styles.hidden)}
            />
        </div>
    );
};